import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge, Button, Card, EmptyState, Table } from '@/admin-design/_common'
import type { BadgeVariant } from '@/admin-design/_common'
import { api } from '../api/client'

export default function Orders() {
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['orders'],
    queryFn: () => api.orders(200),
    refetchInterval: 30000,
  })
  const items: any[] = Array.isArray(data) ? data : []

  const track = useMutation({
    mutationFn: () => api.createJob('track_orders', {}),
    onSuccess: () => {
      setTimeout(() => qc.invalidateQueries({ queryKey: ['orders'] }), 10000)
    },
  })

  const pending = items.filter((o) => !o.tracking_no)
  const shipped = items.filter((o) => !!o.tracking_no)

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">발주 추적</h1>
        <div className="flex items-center gap-3">
          {track.isSuccess && (
            <span className="text-xs text-success animate-pulse">확인 요청됨 — 잠시 후 새로고침됩니다</span>
          )}
          {track.isError && (
            <span className="text-xs text-destructive">요청 실패</span>
          )}
          <Button
            size="sm"
            variant="primary"
            onClick={() => track.mutate()}
            loading={track.isPending}
          >
            지금 확인
          </Button>
        </div>
      </div>

      {/* 미발송 */}
      <Card>
        <Card.Header>
          <Card.Title>
            송장 미등록 <Badge variant="warning" className="ml-2">{pending.length}</Badge>
          </Card.Title>
        </Card.Header>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>주문번호</Table.Header>
                <Table.Header>상품명</Table.Header>
                <Table.Header className="text-right">수량</Table.Header>
                <Table.Header>상태</Table.Header>
                <Table.Header>주문일</Table.Header>
                <Table.Header>업데이트</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {pending.map((o: any) => (
                <Table.Row key={o.id}>
                  <Table.Cell className="font-mono text-xs">{o.order_no}</Table.Cell>
                  <Table.Cell className="text-sm max-w-xs truncate">{o.product_name}</Table.Cell>
                  <Table.Cell className="text-right">{o.quantity}</Table.Cell>
                  <Table.Cell><StatusBadge status={o.order_status} /></Table.Cell>
                  <Table.Cell className="text-xs text-muted-foreground">{o.order_date}</Table.Cell>
                  <Table.Cell className="text-xs text-muted-foreground">{o.updated_at}</Table.Cell>
                </Table.Row>
              ))}
              {pending.length === 0 && (
                <Table.Row className="hover:bg-transparent">
                  <Table.Cell colSpan={6} className="p-0">
                    <EmptyState title="대기 중인 발주가 없습니다" />
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </Card>

      {/* 발송 완료 */}
      <Card>
        <Card.Header>
          <Card.Title>
            송장 등록됨 <Badge variant="success" className="ml-2">{shipped.length}</Badge>
          </Card.Title>
        </Card.Header>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>주문번호</Table.Header>
                <Table.Header>상품명</Table.Header>
                <Table.Header className="text-right">수량</Table.Header>
                <Table.Header>상태</Table.Header>
                <Table.Header>택배사</Table.Header>
                <Table.Header>송장번호</Table.Header>
                <Table.Header>알림</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {shipped.map((o: any) => (
                <Table.Row key={o.id}>
                  <Table.Cell className="font-mono text-xs">{o.order_no}</Table.Cell>
                  <Table.Cell className="text-sm max-w-xs truncate">{o.product_name}</Table.Cell>
                  <Table.Cell className="text-right">{o.quantity}</Table.Cell>
                  <Table.Cell><StatusBadge status={o.order_status} /></Table.Cell>
                  <Table.Cell className="text-sm">{o.courier}</Table.Cell>
                  <Table.Cell className="font-mono text-xs">{o.tracking_no}</Table.Cell>
                  <Table.Cell>
                    {o.notified
                      ? <Badge variant="success">전송됨</Badge>
                      : <Badge variant="muted">대기</Badge>}
                  </Table.Cell>
                </Table.Row>
              ))}
              {shipped.length === 0 && (
                <Table.Row className="hover:bg-transparent">
                  <Table.Cell colSpan={7} className="p-0">
                    <EmptyState title="발송된 주문이 없습니다" />
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </Card>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, BadgeVariant> = {
    '입금대기': 'warning',
    '배송준비중': 'info',
    '배송중': 'info',
    '배송완료': 'success',
    '주문취소': 'destructive',
  }
  return <Badge variant={map[status] ?? 'muted'}>{status || '-'}</Badge>
}
